import { ApiResponseError, type ApiClient } from '@tadpoles/api-client'

import type { AuthenticationService } from './authentication/authentication.svelte.js'
import type { RuntimeKind } from './security-context.js'
import type { UserScopedServicesManager } from './user-scoped-services-manager.svelte.js'

export type AccountDeletionManualRemovalKind = 'browser' | 'native-wrapper'

export type AccountDeletionState =
    | { phase: 'idle' }
    | {
          kind: AccountDeletionManualRemovalKind
          phase: 'manual-removal-required'
      }

type AccountDeletionServiceOptions = {
    apiClient: ApiClient
    auth: Pick<
        AuthenticationService,
        'beginAccountDeletionCompletion' | 'finishAccountDeletionCompletion'
    >
    runtimeKind: RuntimeKind
    userScopedServicesManager: Pick<UserScopedServicesManager, 'waitForCurrentSessionTeardown'>
}

export class AccountDeletionService {
    readonly #apiClient: ApiClient
    readonly #auth: AccountDeletionServiceOptions['auth']
    readonly #runtimeKind: RuntimeKind
    readonly #userScopedServicesManager: AccountDeletionServiceOptions['userScopedServicesManager']
    #completionPromise: Promise<boolean> | undefined
    #state = $state.raw<AccountDeletionState>({ phase: 'idle' })

    public constructor(options: AccountDeletionServiceOptions) {
        this.#apiClient = options.apiClient
        this.#auth = options.auth
        this.#runtimeKind = options.runtimeKind
        this.#userScopedServicesManager = options.userScopedServicesManager
    }

    public get state(): AccountDeletionState {
        return this.#state
    }

    public async requestVerificationEmail(signal?: AbortSignal): Promise<void> {
        this.#completionPromise = undefined
        this.#state = { phase: 'idle' }
        await this.#apiClient.account.requestDeletionVerificationEmail({ signal })
    }

    public async deleteAccount(verificationCode: string, signal?: AbortSignal): Promise<void> {
        await this.#apiClient.account.completeDeletion({ token: verificationCode }, { signal })
    }

    public completeDeletionCleanup(): Promise<boolean> {
        this.#completionPromise ??= this.#completeDeletionCleanup()
        return this.#completionPromise
    }

    public dismissManualRemovalNotice(): void {
        if (this.#state.phase !== 'manual-removal-required') {
            return
        }

        this.#state = { phase: 'idle' }
        this.#auth.finishAccountDeletionCompletion(false)
    }

    async #completeDeletionCleanup(): Promise<boolean> {
        const authenticationContextCleanup = this.#auth.beginAccountDeletionCompletion()
        const currentUserCleanup = this.#userScopedServicesManager.waitForCurrentSessionTeardown()
        const [authenticationContextResult, currentUserResult] = await Promise.allSettled([
            authenticationContextCleanup,
            currentUserCleanup
        ])
        const cleanupSucceeded =
            authenticationContextResult.status === 'fulfilled' &&
            authenticationContextResult.value &&
            currentUserResult.status === 'fulfilled' &&
            currentUserResult.value.currentUserPersistenceCleared

        if (cleanupSucceeded) {
            this.#auth.finishAccountDeletionCompletion(true)
            return true
        }

        this.#state = {
            kind: this.#runtimeKind === 'native-wrapper' ? 'native-wrapper' : 'browser',
            phase: 'manual-removal-required'
        }
        return false
    }
}

export function isAccountDeletionCodeError(error: unknown): boolean {
    if (!(error instanceof ApiResponseError)) {
        return false
    }

    switch (error.error?.code) {
        case 'expired_token':
        case 'invalid_token':
        case 'no_token':
            return true
        default:
            return false
    }
}
